import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Menu, X, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import UI from '../lib/uiCatalog';
import LanguageSelector from './LanguageSelector';
import { LLink as Link, LNavLink as NavLink } from './LocalizedLink';

export default function Header() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const T = (k) => t(k, UI[k]);
  const routerNavigate = useNavigate();

  const navItems = [
    { to: '/',                  label: T('nav.home') },
    { to: '/home-insurance',    label: T('nav.home_insurance') },
    { to: '/health-insurance',  label: T('nav.health_insurance') },
    { to: '/about',             label: T('nav.about') },
    { to: '/faq',               label: T('nav.faq') },
    { to: '/blog',              label: T('nav.blog') },
    { to: '/contact',           label: T('nav.contact') },
  ];

  return (
    <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
      <div className="container-x flex items-center justify-between h-[72px]">
        <Link to="/" className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-full bg-[#0d3a8a] text-white inline-flex items-center justify-center">
            <Eye size={18} />
          </span>
          <span className="font-bold text-[#0b1f3a] text-lg tracking-tight">EasySpanishInsurance</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/'}
              className={({ isActive }) =>
                `text-sm font-medium whitespace-nowrap transition-colors ${
                  isActive ? 'text-[#0d3a8a]' : 'text-slate-600 hover:text-[#0d3a8a]'
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageSelector />
          {user ? (
            <div className="flex items-center gap-2">
              <button
                onClick={() => routerNavigate('/dashboard')}
                className="btn-outline !py-2 !px-4 text-sm"
              >
                <User size={16} /> {user.name}
              </button>
              <button onClick={logout} className="text-sm text-slate-500 hover:text-slate-800">
                {T('nav.logout')}
              </button>
            </div>
          ) : (
            <Link to="/login" className="btn-outline !py-2 !px-6 text-sm">{T('nav.login')}</Link>
          )}
        </div>

        <button className="lg:hidden p-2" onClick={() => setOpen((o) => !o)} aria-label="Menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white" data-testid="mobile-menu">
          <div className="container-x py-3 flex flex-col gap-1">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === '/'}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-2 py-2 rounded-md text-sm ${
                    isActive ? 'bg-slate-100 text-[#0d3a8a]' : 'text-slate-700 hover:bg-slate-50'
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div
              className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-3"
              data-testid="mobile-menu-language-row"
            >
              <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                {T('nav.language') || 'Language'}
              </span>
              <LanguageSelector onChanged={() => setOpen(false)} />
            </div>
            {user ? (
              <button onClick={() => { setOpen(false); routerNavigate('/dashboard'); }} className="btn-outline mt-3 justify-center">{T('nav.dashboard')}</button>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="btn-outline mt-3 justify-center">{T('nav.login')}</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
